import React, { useContext, useEffect } from "react";

import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import FileIcon from "../../../../assets/FileIconBlue.svg";
import ViewIcon from "../../../../assets/ViewIcon.svg";
import DownloadIcon from "../../../../assets/DownloadBlueIcon.svg";
import DeleteIcon from "../../../../assets/DeleteRedIcon.svg";
import {
  fetchWaiverRequestDoc,
  patchSaveApplication,
} from "../../../../services/network";
import { useParams } from "react-router-dom";
import { ParamType } from "../LoansPagev2";
import { getCookie } from "../../../../utils/Common";
import { LoansContext } from "../../../../context/LoansContext";
import { useFilePreview } from "../../../../hooks/useFilePreview";
import { FilePreviewModal } from "../../../../components/filePreview/FilePreviewModal";

export default function DocumentList(props: any) {
  const params = useParams<ParamType>();
  const loanContextData = useContext(LoansContext);

  const { setLoanDetails, loanDetails } = loanContextData;
  const { dynamicConfig, isLoanSubmitted, setDocumentList, documentList } = props;
  const { loanId } = params;

  const { previewData, loading, error, openPreview, closePreview } = useFilePreview();

  async function viewFile(doc: any) {
    const urlData = await fetchWaiverRequestDoc(
      dynamicConfig?.fileDownloadUrl,
      {
        path: doc?.documentPath,
      },
    );

    openPreview(urlData.data, doc.documentName);
  }

  async function handleDownloadDocument(doc: any) {
    try {
      const urlData = await fetchWaiverRequestDoc(
        dynamicConfig?.fileDownloadUrl,
        {
          path: doc?.documentPath,
        },
      );
      window.open(urlData.data, "_blank");
    } catch (error) {
      console.error("Error downloading document:", error);
    }
  }

  async function handleDeleteDocument(doc: any) {
    try {
      // Soft delete: Mark the document as deleted without removing it from the array
      const updatedDocuments = documentList.map((d: any) => {
        if (
          d.id === doc.id ||
          (d.documentName === doc.documentName &&
            d.documentPath === doc.documentPath)
        ) {
          return {
            ...d,
            isDeleted: true,
            deletedOn: new Date(),
            deletedBy: getCookie("partyId") ?? "unknown", // Assuming you have a way to get the current user's ID
          };
        }
        return d;
      });
      // Update the documents state with the soft-deleted document
      // This assumes you have a state management solution in place to handle the documents array
      // For example, if documents are managed in a parent component, you would call a prop function to update it
      // props.updateDocuments(updatedDocuments);
      await patchSaveApplication(loanId, { documents: updatedDocuments });
      setLoanDetails((prevData: any) => ({
        ...prevData,
        documents: updatedDocuments,
      }));
      setDocumentList(updatedDocuments);
    } catch (error) {
      console.error("Error deleting document:", error);
    }
  }

  return (
    <Box
      sx={{
        borderRadius: "14px",
        border: "1px solid #DBE2FF",
        background: "#FFF",
        display: "block",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: "24px 24px 0 24px",
        gap: 16,
      }}
    >
      <h3
        style={{
          color: "#000",
          fontFamily: "Poppins",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 500,
          lineHeight: "24px",
        }}
      >
        Uploaded Documents
      </h3>
      <List sx={{ width: "100%", bgcolor: "background.paper" }}>
        {documentList
          ?.filter((doc: any) => !doc.isDeleted)
          ?.map((doc: any, index: number) => (
            <Paper
              key={doc.id}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2, borderColor: "#e0e0e0" }}
            >
              <ListItem
                secondaryAction={
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      edge="end"
                      aria-label="view"
                      color="primary"
                      onClick={() => viewFile(doc)}
                    >
                      <img src={ViewIcon} alt="View" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="download"
                      color="primary"
                      onClick={() => handleDownloadDocument(doc)}
                    >
                      <img src={DownloadIcon} alt="Download" />
                    </IconButton>
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      color="error"
                      onClick={() => handleDeleteDocument(doc)}
                      disabled={!!isLoanSubmitted} // Disable delete if loan is submitted
                    >
                      <img src={DeleteIcon} alt="Delete" />
                    </IconButton>
                  </Stack>
                }
              >
                <ListItemIcon>
                  <img
                    src={FileIcon}
                    alt="File"
                    style={{
                      marginRight: "16px",
                      height: "38px",
                      width: "38px",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: "#000",
                        fontFamily: "Poppins",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 500,
                        lineHeight: "21px",
                      }}
                    >
                      {doc?.documentName ?? ""}
                    </Typography>
                  }
                  secondary={
                    <Typography
                      sx={{
                        color: "#666",
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        fontStyle: "normal",
                        fontWeight: 300,
                        lineHeight: "18px",
                      }}
                    >
                      {doc?.date
                        ? new Date(doc.date).toLocaleDateString()
                        : "26 March, 2026, 11:30 AM"}
                    </Typography>
                  }
                />
              </ListItem>
            </Paper>
          ))}
      </List>
      {/* <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Preview: {currentDoc?.documentName ?? ""}
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ height: "80vh", p: 0 }}>
          <iframe
            src={fileURL ?? ""}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="Document Preview"
          />
        </DialogContent>
      </Dialog> */}
      <FilePreviewModal
        open={loading || !!previewData || !!error}
        loading={loading}
        error={error}
        previewData={previewData}
        onClose={closePreview}
      />
    </Box>
  );
}
